import { useEffect } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { ClientsList } from '../components/clients/ClientsList.js';
import { useClientsQuery } from '../queries/use-clients.js';

/**
 * `/clients` page — split layout matching the legacy `wf-adminconsole`.
 *
 * - **Left**: {@link ClientsList} sidebar (search + Add New + clickable
 *   list).
 * - **Right**: outlet for `/clients/:id/...` child routes. When the URL
 *   doesn't include a client id, we either redirect to the first
 *   loaded tenant (preserving the legacy "auto-select" UX) or show an
 *   empty hint when the list is empty.
 */
export function Clients(): JSX.Element {
  return (
    <div className="flex h-[calc(100vh-72px)] flex-row">
      <ClientsList />
      <main className="flex grow flex-col overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Index route under `/clients` — redirects to the first tenant once the
 * list loads. Until then we show a neutral hint.
 */
export function ClientsIndex(): JSX.Element | null {
  const { data, isLoading } = useClientsQuery();
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id && data && data.length > 0) {
      const first = [...data].sort((a, b) => a.name.localeCompare(b.name))[0];
      if (first) navigate(`/clients/${first.id}`, { replace: true });
    }
  }, [id, data, navigate]);

  if (isLoading) {
    return (
      <div className="flex grow items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        <span role="status">Loading clients…</span>
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="flex grow flex-col items-center justify-center p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200">No clients yet</h2>
        <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
          Onboard a tenant from the sidebar to start configuring licenses, OAuth apps, and Workfront
          connections.
        </p>
      </div>
    );
  }
  // While the redirect runs the list is non-empty — render nothing (the
  // navigate happens in the effect above).
  return null;
}
